import React, { Fragment, useEffect, useState } from 'react';
import { func, object } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { length, path, pathOr } from 'ramda';
import { Form as FinalForm } from 'react-final-form';

import config from '../../config';
import { ensureCurrentUser, ensureStripeCustomer, ensurePaymentMethodCard } from '../../util/data';
import { propTypes } from '../../util/types';
import {
  getCountryValue,
  getStripeCustomerForPaymentMethodSave,
  isUsableStripePaymentMethod,
  shouldForceCartesBancairesResave,
} from '../../util/stripe';
import { savePaymentMethod, deletePaymentMethod } from '../../ducks/paymentMethods.duck';
import { handleCardSetup } from '../../ducks/stripe.duck';
import { manageDisableScrolling } from '../../ducks/UI.duck';
import {
  Divider,
  DummyTopbar, FieldStepperEditable,
  FieldTextInput,
  HeadingWithBackButton,
  Page,
  PrimaryButton,
  SavedCardDetails,
} from '../../components';
import { PaymentMethodsForm } from '../../forms';

import {
  createStripeSetupIntent,
  stripeCustomer,
} from '../AccountPaymentsPage/components/PaymentMethodsTab/PaymentMethodsTab.duck.js';
import css from './CheckoutPageMultiBooking.module.css';
import { injectIntl } from '../../util/reactIntl';
import CancelationPolicyInfo
  from '../../forms/StripePaymentForm/CancelationPolicyInfo/CancelationPolicyInfo';
import ConfirmationInfo from '../../forms/StripePaymentForm/ConfirmationInfo/ConfirmationInfo';
import { FormattedMessage } from 'react-intl';
import * as validators from '../../util/validators';
import Form from '../../components/Form/Form';
import { getSuggestionSearchParamsStringify } from '../../util/search';
import { initiateMultiBooking } from './CheckoutPageMultiBooking.duck';
import { initiateOrder, verifyDiscountCode } from '../CheckoutPage/CheckoutPage.duck';
import { useHistory, useLocation } from 'react-router-dom';
import { parse } from '../../util/urlHelpers';
import routeConfiguration from '../../routeConfiguration';
import { createResourceLocatorString } from '../../util/routes';
import { clearData } from '../../util/checkoutSessionHelpers';
import DiscountCode from '../../forms/StripePaymentForm/DiscountCode/DiscountCode';

const CheckoutPageMultiBooking = props => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [cardState, setCardState] = useState(null);
  const [hasValidPaymentMethod, setHasValidPaymentMethod] = useState(false);
  const {
    currentUser,
    addPaymentMethodError,
    deletePaymentMethodError,
    createStripeCustomerError,
    handleCardSetupError,
    deletePaymentMethodInProgress,
    onCreateSetupIntent,
    onHandleCardSetup,
    onSavePaymentMethod,
    onDeletePaymentMethod,
    fetchStripeCustomer,
    onManageDisableScrolling,
    stripeCustomerFetched,
    onInitiateMultiBooking,
    intl,
    listingsIds,
    cartBookingParams,
    isLoading,
    onVerifyDiscountCode,
    discount,
    errorVerifyDiscountCode,
    stripePaymentMethodDetails,
  } = props;
  const history = useHistory();
  const location = useLocation();
  const { search } = location;
  const parsedSearch = parse(search);

  useEffect(() => {
    fetchStripeCustomer();
  }, [fetchStripeCustomer]);

  const ensuredCurrentUser = ensureCurrentUser(currentUser);
  const currentUserLoaded = !!ensuredCurrentUser.id;
  const hasDefaultPaymentMethod =
    currentUser &&
    ensureStripeCustomer(currentUser.stripeCustomer).attributes.stripeCustomerId &&
    ensurePaymentMethodCard(currentUser.stripeCustomer.defaultPaymentMethod).id;
  const hasUsablePaymentMethod = isUsableStripePaymentMethod(stripePaymentMethodDetails);
  const hasSetupIntent = !!path(['attributes', 'profile', 'privateData', 'clientSecretSetupIntent'], currentUser);
  const requiresCardResave = shouldForceCartesBancairesResave(stripePaymentMethodDetails);
  useEffect(() => {
    setHasValidPaymentMethod(
      hasDefaultPaymentMethod && hasUsablePaymentMethod && hasSetupIntent && !requiresCardResave
    );
  }, [hasSetupIntent, hasDefaultPaymentMethod, hasUsablePaymentMethod, requiresCardResave]);
  const getClientSecret = setupIntent => {
    return setupIntent && setupIntent.attributes ? setupIntent.attributes.clientSecret : null;
  };
  const getPaymentParams = (currentUser, formValues) => {
    const { name, addressLine1, addressLine2, postal, state, city, country } = formValues;
    const addressMaybe =
      addressLine1 && postal
        ? {
          address: {
            city: city,
            country: country,
            line1: addressLine1,
            line2: addressLine2,
            postal_code: postal,
            state: state,
          },
        }
        : {};
    const billingDetails = {
      name,
      email: ensureCurrentUser(currentUser).attributes.email,
      ...addressMaybe,
    };

    const paymentParams = {
      payment_method_data: {
        billing_details: billingDetails,
      },
    };

    return paymentParams;
  };

  const handleSubmitCard = params => {
    setIsSubmitting(true);
    const ensuredCurrentUser = ensureCurrentUser(currentUser);
    const stripeCustomer = ensuredCurrentUser.stripeCustomer;
    const stripeCustomerForSave = getStripeCustomerForPaymentMethodSave(
      stripeCustomer,
      stripePaymentMethodDetails
    );
    const { stripe, card, formValues } = params;

    onCreateSetupIntent()
      .then(setupIntent => {
        const stripeParams = {
          stripe,
          card,
          setupIntentClientSecret: getClientSecret(setupIntent),
          paymentParams: getPaymentParams(currentUser, formValues),
        };

        return onHandleCardSetup(stripeParams);
      })
      .then(result => {
        const newPaymentMethod = result.setupIntent.payment_method;
        const currentUserEmail = path(['attributes', 'email'], currentUser);
        // Note: stripe.handleCardSetup might return an error inside successful call (200), but those are rejected in thunk functions.

        return onSavePaymentMethod(stripeCustomerForSave, newPaymentMethod, currentUserEmail);
      })
      .then(() => {
        // Update currentUser entity and its sub entities: stripeCustomer and defaultPaymentMethod
        fetchStripeCustomer();
        setIsSubmitting(false);
        setCardState('default');
      })
      .catch(error => {
        console.error(error);
        setIsSubmitting(false);
      });
  };
  const handleRemovePaymentMethod = () => {
    onDeletePaymentMethod().then(() => {
      fetchStripeCustomer();
    });
  };
  const handleSubmit = async ({ initialMessage }) => {
    const { extras, hasSelectedSwimPlus } = parsedSearch;
    const adults = cartBookingParams?.numberOfPeople?.numberOfAdults ?? 0;
    const children = cartBookingParams?.numberOfPeople?.numberOfChildren ?? 0;
    const infants = cartBookingParams?.numberOfPeople?.numberOfInfants ?? 0;
    const orderParams = {
      listingsIds,
      extras: JSON.parse(extras),
      bookingStart: cartBookingParams.startAt,
      bookingEnd: cartBookingParams.endAt,
      protectedData: {
        hasSelectedSwimPlus,
        initiatedFromMobile: false,
        bookingStartDisplay: cartBookingParams.startAt,
        bookingEndDisplay: cartBookingParams.endAt,
        // suggestionSearchParams,
        platform: 'WEB',
        date: cartBookingParams.startAt,
        endDate: cartBookingParams.endAt,
        quantity: {
          adults,
          children,
          infants,
        },
        //TODO: should calculate quantityHours dynamically
        quantityHours: cartBookingParams.nbHoursSelected,
        // paidExtrasData,
      },
      quantity1: adults,
      quantity2: children,
      quantity3: infants,
    };
    const res = await onInitiateMultiBooking(orderParams, initialMessage);
    const routes = routeConfiguration();
    const congratPurchaseBookingPageDetailsPath = createResourceLocatorString(
      'CongratMultiBookingPage',
      routes,
      {},
      { isMultiBooking: length(listingsIds) > 1 },
    );
    history.push(congratPurchaseBookingPageDetailsPath);
    // await sendinitialMessage
  };

  // Get first and last name of the current user and use it in the StripePaymentForm to autofill the name field
  const userName = currentUserLoaded
    ? `${ensuredCurrentUser.attributes.profile.firstName} ${ensuredCurrentUser.attributes.profile.lastName}`
    : null;
  const countryValue = getCountryValue(config.locale);

  const initalValuesForStripePayment = { name: userName, country: countryValue };

  const card = hasDefaultPaymentMethod
    ? ensurePaymentMethodCard(currentUser.stripeCustomer.defaultPaymentMethod).attributes.card
    : null;

  const showForm = cardState === 'replaceCard' || !hasDefaultPaymentMethod || !hasValidPaymentMethod;
  const showCardDetails = !!hasDefaultPaymentMethod && hasUsablePaymentMethod && !requiresCardResave;
  // const shouldRenderCriteoBasketTag = currentUser && currentListing && !isNil(tx.id);
  // const criteoBasketTag = shouldRenderCriteoBasketTag && (
  //   <CriteoBasketCart
  //     currentUser={currentUser}
  //     currentListing={currentListing}
  //     currentTransaction={tx}
  //   />
  // );
  const title = intl.formatMessage({ id: 'CheckoutPage.heading' });
  const pageProps = { title, scrollingDisabled: false };
  const submitText = intl.formatMessage({ id: 'StripePaymentForm.submitConfirmPaymentInfo' });
  const infoHeadingText = intl.formatMessage({ id: 'MultiBooking.EnquiryPanel.messageHeading' });
  const infoText = intl.formatMessage({ id: 'MultiBooking.InitialMessageSection.info' });
  const messagePlaceholder = intl.formatMessage({ id: 'MultiBooking.messagePlaceholder' });
  const initialMessageLabel = intl.formatMessage({ id: 'StripePaymentForm.messageLabel' });
  const initalMessageRequiredText = intl.formatMessage({ id: 'StripePaymentForm.messageRequired' });
  const initialMessageRequired = validators.required(initalMessageRequiredText);

  return (
    <Page className={css.root} {...pageProps}>
      {/*{criteoBasketTag}*/}
      <DummyTopbar className={css.topbarDesktop} />
      <div className={css.mainContent}>
        <HeadingWithBackButton className={css.headingWithBackButton} heading={title} />
        <div className={css.bodyContainer}>
          <div>
            {hasValidPaymentMethod && showCardDetails ? (
              <SavedCardDetails
                card={card}
                onManageDisableScrolling={onManageDisableScrolling}
                onChange={setCardState}
                onDeleteCard={handleRemovePaymentMethod}
                deletePaymentMethodInProgress={deletePaymentMethodInProgress || isLoading}
              />
            ) : null}
            {showForm ? (
              <div className={css.PaymentMethodsForm}>
                <PaymentMethodsForm
                  className={css.paymentForm}
                  formId="PaymentMethodsForm"
                  initialValues={initalValuesForStripePayment}
                  onSubmit={handleSubmitCard}
                  handleRemovePaymentMethod={handleRemovePaymentMethod}
                  hasDefaultPaymentMethod={hasDefaultPaymentMethod}
                  addPaymentMethodError={addPaymentMethodError}
                  deletePaymentMethodError={deletePaymentMethodError}
                  createStripeCustomerError={createStripeCustomerError}
                  handleCardSetupError={handleCardSetupError}
                  inProgress={isSubmitting || isLoading}
                />
              </div>
            ) : null}
          </div>
          {hasValidPaymentMethod && (
            <DiscountCode
              className={css.discountCode}
              discount={discount}
              errorVerifyDiscountCode={errorVerifyDiscountCode}
              intl={intl}
              onVerifyDiscountCode={onVerifyDiscountCode}
            />
          )}
          {hasValidPaymentMethod && (
            <FinalForm
              onSubmit={handleSubmit}
              render={formRenderProps => {
                const {
                  active,
                  form,
                  handleSubmit,
                  values,
                } = formRenderProps;

                return (
                  <Form onSubmit={handleSubmit}>
                    <div className={css.initialMessageContainer}>
                      <div>
                        <Divider className={css.divider} />
                        <div className={css.initialMessageSection}>
                          <h3 className={css.heading}>
                            <FormattedMessage id="StripePaymentForm.messageHeading" />
                          </h3>
                          <div className={css.infoWrapper}>
                            <span className={css.infoHeading}>{infoHeadingText}</span>
                            <span className={css.info}>{infoText}</span>
                          </div>
                          <FieldTextInput
                            className={css.fieldMessage}
                            type="textarea"
                            id={'initialMessage'}
                            name="initialMessage"
                            label={initialMessageLabel}
                            placeholder={messagePlaceholder}
                            validate={initialMessageRequired}
                          />
                        </div>
                        <Divider className={css.divider} />
                        <CancelationPolicyInfo
                          className={css.cancelationPolicyInfo}
                          intl={intl}
                        />
                        <Divider className={css.divider} />
                        <ConfirmationInfo
                          className={css.confirmationInfo}
                          isMultiBooking
                          intl={intl}
                          numberOfListings={length(listingsIds)}
                        />
                      </div>
                      <Divider className={css.divider} />
                      <div className={css.submitContainer}>
                        {/*{hasPaymentErrors ? (*/}
                        {/*  <span className={css.errorMessage}>{paymentErrorMessage}</span>*/}
                        {/*) : null}*/}
                        <PrimaryButton
                          className={css.submitButton}
                          type="submit"
                          inProgress={isLoading}
                          disabled={isLoading}
                        >
                          <span>{submitText}</span>
                        </PrimaryButton>
                      </div>
                    </div>
                  </Form>
                );
              }}
            />
          )}
        </div>
      </div>
    </Page>
  );
};

CheckoutPageMultiBooking.defaultProps = {
  currentUser: null,
  addPaymentMethodError: null,
  deletePaymentMethodError: null,
  createStripeCustomerError: null,
  handleCardSetupError: null,
};

CheckoutPageMultiBooking.propTypes = {
  currentUser: propTypes.currentUser,
  addPaymentMethodError: object,
  deletePaymentMethodError: object,
  createStripeCustomerError: object,
  handleCardSetupError: object,
  onCreateSetupIntent: func.isRequired,
  onHandleCardSetup: func.isRequired,
  onSavePaymentMethod: func.isRequired,
  onDeletePaymentMethod: func.isRequired,
  fetchStripeCustomer: func.isRequired,
};

const mapStateToProps = state => {
  const { currentUser } = state.user;
  const {
    deletePaymentMethodInProgress,
    addPaymentMethodError,
    deletePaymentMethodError,
    createStripeCustomerError,
  } = state.paymentMethods;
  const {
    discount,
    errorVerifyDiscountCode,
  } = state.CheckoutPage;
  const { isLoading } = state.CheckoutPageMultiBooking;
  const { stripeCustomerFetched, stripePaymentMethodDetails } = state.PaymentMethodsTab;
  const { handleCardSetupError } = state.stripe;
  const listingsIds = pathOr([], ['attributes', 'profile', 'publicData', 'addToCart'], currentUser);
  const cartBookingParams = pathOr({}, ['attributes', 'profile', 'privateData', 'cartBookingParams'], currentUser);

  return {
    isLoading,
    currentUser,
    listingsIds,
    cartBookingParams,
    deletePaymentMethodInProgress,
    addPaymentMethodError,
    deletePaymentMethodError,
    createStripeCustomerError,
    handleCardSetupError,
    stripeCustomerFetched,
    stripePaymentMethodDetails,
    discount: discount.discount,
    errorVerifyDiscountCode,
  };
};

const mapDispatchToProps = dispatch => ({
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
  fetchStripeCustomer: () => dispatch(stripeCustomer()),
  onHandleCardSetup: params => dispatch(handleCardSetup(params)),
  onCreateSetupIntent: params => dispatch(createStripeSetupIntent(params)),
  onSavePaymentMethod: (stripeCustomer, newPaymentMethod, stripeCustomerEmail) =>
    dispatch(savePaymentMethod(stripeCustomer, newPaymentMethod, stripeCustomerEmail)),
  onDeletePaymentMethod: params => dispatch(deletePaymentMethod(params)),
  onInitiateMultiBooking: (params, message) => dispatch(initiateMultiBooking(params, message)),
  onVerifyDiscountCode: (params) => dispatch(verifyDiscountCode(params)),
});

export default compose(
  connect(
    mapStateToProps,
    mapDispatchToProps,
  ),
  injectIntl,
)(CheckoutPageMultiBooking);
